"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { ExternalLink } from "lucide-react";
import { formatCurrency, formatOdds, isValidAmericanOdds } from "@/lib/whale-watch/whale-watch-shared";

export function BookLiquidityLabel({
  amount,
  className,
}: {
  amount: number | null | undefined;
  className?: string;
}) {
  if (amount == null || !Number.isFinite(amount) || amount <= 0) return null;
  return (
    <span className={cn("text-[10px] leading-3 font-medium tabular-nums text-muted-foreground", className)}>
      {formatCurrency(amount, true)}
    </span>
  );
}

function PeerLiquidity({ amount }: { amount: number | null | undefined }) {
  return <BookLiquidityLabel amount={amount} className="text-right" />;
}

function IconSlot({ size }: { size: number }) {
  return <span aria-hidden className="shrink-0" style={{ width: size }} />;
}

function NoPrice({ className }: { className: string }) {
  return (
    <span
      role="img"
      aria-label="No price"
      className={cn(className, "tabular-nums data-num font-medium text-zinc-500")}
    >
      -
    </span>
  );
}

export function PeerOddsCell({
  odds,
  liquidity,
  betLink,
  hasEdge = false,
  isPlayBook = false,
  variant = "desktop",
  onBetLinkClick,
}: {
  odds?: number;
  liquidity?: number | null;
  betLink?: string;
  hasEdge?: boolean;
  isPlayBook?: boolean;
  variant?: "desktop" | "mobile";
  onBetLinkClick?: () => void;
}) {
  const textSize = isPlayBook ? "text-base" : "text-[15px]";
  const iconSize = variant === "desktop" ? 12 : 10;
  const gapClass = variant === "desktop" ? "gap-1" : "gap-0.5";

  if (odds == null || !isValidAmericanOdds(odds)) {
    return (
      <span className={cn("flex items-center", gapClass)}>
        <NoPrice className={textSize} />
        <IconSlot size={iconSize} />
      </span>
    );
  }

  const oddsRow = (
    <>
      <span
        className={cn(
          textSize,
          "tabular-nums data-num",
          isPlayBook
            ? "font-semibold text-accent-value"
            : hasEdge
              ? "font-medium text-accent-value-strong"
              : "font-medium text-zinc-300"
        )}
      >
        {formatOdds(odds)}
      </span>
      {betLink ? (
        <ExternalLink
          size={iconSize}
          className={cn(
            "transition-colors",
            hasEdge
              ? variant === "desktop"
                ? "text-accent-value/50 group-hover/betlink:text-accent-value"
                : "text-accent-value/50"
              : variant === "desktop"
                ? "text-zinc-500 group-hover/betlink:text-zinc-300"
                : "text-zinc-500"
          )}
        />
      ) : (
        <IconSlot size={iconSize} />
      )}
    </>
  );
  const liquidityRow = (
    <span className={cn("flex items-center", gapClass)}>
      <PeerLiquidity amount={liquidity} />
      <IconSlot size={iconSize} />
    </span>
  );

  if (betLink) {
    return (
      <a
        href={betLink}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          e.stopPropagation();
          onBetLinkClick?.();
        }}
        className={cn(
          "group/betlink flex flex-col items-end transition-colors",
          variant === "mobile" && "min-h-[44px] w-full h-full justify-center",
          variant === "desktop" && "hover:brightness-110"
        )}
      >
        <span className={cn("flex items-center", gapClass)}>{oddsRow}</span>
        {liquidityRow}
      </a>
    );
  }

  return (
    <span className="flex flex-col items-end">
      <span className={cn("flex items-center", gapClass)}>{oddsRow}</span>
      {liquidityRow}
    </span>
  );
}

export function PeerOddsCellMuted({
  odds,
  betLink,
  variant = "desktop",
  onBetLinkClick,
}: {
  odds?: number;
  betLink?: string;
  variant?: "desktop" | "mobile";
  onBetLinkClick?: () => void;
}) {
  const textSize = variant === "desktop" ? "text-[15px]" : "text-sm";
  const iconSize = variant === "desktop" ? 12 : 10;
  const gapClass = variant === "desktop" ? "gap-1" : "gap-0.5";

  if (odds == null || !isValidAmericanOdds(odds)) {
    return (
      <span className={cn("flex items-center", gapClass)}>
        <NoPrice className={textSize} />
        <IconSlot size={iconSize} />
      </span>
    );
  }

  if (betLink) {
    return (
      <a
        href={betLink}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          e.stopPropagation();
          onBetLinkClick?.();
        }}
        className={cn(
          "group/betlink flex items-center transition-colors",
          gapClass,
          variant === "mobile" && "min-h-[44px] w-full h-full justify-end",
          variant === "desktop" && "hover:brightness-110"
        )}
      >
        <span className={cn(textSize, "tabular-nums data-num font-medium text-zinc-500", variant === "desktop" && "group-hover/betlink:text-zinc-300")}>
          {formatOdds(odds)}
        </span>
        <ExternalLink
          size={iconSize}
          className={cn(
            "transition-colors",
            variant === "desktop"
              ? "text-zinc-600 group-hover/betlink:text-zinc-400"
              : "text-zinc-600"
          )}
        />
      </a>
    );
  }

  return (
    <span className={cn("flex items-center", gapClass)}>
      <span className={cn(textSize, "tabular-nums data-num font-medium text-zinc-500")}>
        {formatOdds(odds)}
      </span>
      <IconSlot size={iconSize} />
    </span>
  );
}
